import React, { useEffect, useState } from "react";
import axios from "axios";
import { Spin } from "antd";
import config from "../config";
import { useCurrency } from "../contexts/ValidationContext";
import { getCurrencySymbol } from "../utils/currencyHelper";
import { getPaymentData } from "../utils/paymentData";
import paymentMethodMap from "../utils/paymentMethodMap"; // Import the payment method mapping

const CustomerReceipt = ({ invoiceId, tableNumber, takeAwayTable, services, pax, paymentData }) => {
  const apiBaseUrl = config.REACT_APP_API_BASE_URL;
  const [restaurantName, setRestaurantName] = useState(null);
  const [restaurantAddress, setRestaurantAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoiceData, setInvoiceData] = useState(null);

  const {
    totalWithoutTaxes,
    discount,
    discountAmount,
    discountType,
    totalAfterDiscount,
    taxes,
    totalTaxes,
    totalWithTaxes,
    totalWithServices,
    rounding,
    roundedTotalWithServices,
    deposit,
    payableAmount,
    depositMethod,
    cashAmount,
    balance,
    paymentMethod,
    transactionId,
    remarks,
    currentCashierName,
  } = paymentData || getPaymentData();

  const { currency } = useCurrency();
  const symbol = getCurrencySymbol(currency);

  useEffect(() => {
    setLoading(true);
    axios.get(`${apiBaseUrl}/api/settings/get-settings`).then(({ data }) => {
      setRestaurantName(data.name);
      setRestaurantAddress(data.address);
    }).finally(() => setLoading(false));
  }, [apiBaseUrl]);

  useEffect(() => {
    if (!invoiceId) return;
    setLoading(true);
    axios.get(`${apiBaseUrl}/api/payments/${invoiceId}`)
      .then(({ data }) => setInvoiceData(data))
      .finally(() => setLoading(false));
  }, [invoiceId, apiBaseUrl]);

  if (loading) {
    return <div style={{ textAlign: "center", marginTop: "50px" }}><Spin size="large" /></div>;
  }
  if (!invoiceData && paymentData && paymentData.isPreview) {
    // Show preview using the same layout as the final receipt, but with a PREVIEW RECEIPT banner
    let previewItemRows = [];
    if (paymentData.orderDetails && Array.isArray(paymentData.orderDetails)) {
      previewItemRows = Object.entries(
        paymentData.orderDetails.flatMap(order => Array.isArray(order.order_items) ? order.order_items : []).reduce((acc, item) => {
          const addOnNames = item.product.addOns.map(a => a.name).sort().join(", ");
          const key = `${item.product.name}-${item.remarks || ""}-${addOnNames}`;
          acc[key] = acc[key] || {
            productName: item.product.name,
            addOns: item.product.addOns,
            remark: item.remarks,
            totalQuantity: 0,
            discountAble: Boolean(item.product.discountAble !== false),
            price: Number(item.product.price),
            productCategory: item.product.category,
          };
          acc[key].totalQuantity += Number(item.quantity);
          return acc;
        }, {})
      );
    }
    // Service rows
    const previewServiceRows = (services || []).filter(s => s.quantity > 0).map((service, idx) => (
      <tr key={"service-" + idx}>
        <td>{service.name} **</td>
        <td>{service.quantity}</td>
        <td style={{ textAlign: "right" }}>{service.totalPrice?.toFixed ? service.totalPrice.toFixed(2) : Number(service.totalPrice || 0).toFixed(2)}</td>
      </tr>
    ));
    // Calculate payable amount for preview
    const previewGrandTotal = parseFloat(paymentData.roundedTotalWithServices) || 0;
    const previewDeposit = parseFloat(paymentData.deposit) || 0;
    const previewPayableAmount = (previewGrandTotal - previewDeposit).toFixed(2);
    return (
      <div className="customer-receipt-modal-bg">
        <div className="customer-receipt-modal">
          <div className="receipt-items-table">
            <style>{`
              .customer-receipt-modal-bg {
                position: fixed;
                top: 0; left: 0;
                width: 100%; height: 100%;
                min-height: 0;
                z-index: 9999;
                background: rgba(255,255,255,0.0); /* transparent, video visible */
                display: flex;
                align-items: center;
                justify-content: center;
                pointer-events: auto;
              }
              .customer-receipt-modal {
                background: #fff;
                border-radius: 18px;
                box-shadow: 0 8px 32px rgba(0,0,0,0.18);
                max-width: 900px;
                width: 90%;
                max-height: 90%;
                min-width: 350px;
                display: flex;
                flex-direction: row;
                overflow: hidden;
                height: 90%;
                min-height: 0;
                position: relative;
                z-index: 10000;
              }
              .receipt-items-table {
                flex: 2;
                padding: 5px 20px 24px 20px;
                display: flex;
                flex-direction: column;
                height: 100%;
                min-height: 0;
                overflow: hidden;
              }
              .receipt-items-table .table-scroll {
                flex: 1 1 auto;
                overflow-y: auto;
                overflow-x: hidden;
              }
              .receipt-items-table .info-bottom {
                flex-shrink: 0;
              }
              .receipt-items-table table {
                width: 100%;
                border-collapse: collapse;
                table-layout: fixed;
              }
              .receipt-items-table th, .receipt-items-table td {
                padding: 8px 6px;
                font-size: 1.1em;
                border-bottom: 1px solid #eee;
                text-align: left;
                word-wrap: break-word;
                overflow-wrap: break-word;
              }
              .receipt-items-table th {
                font-weight: bold;
                background: #f7f7f7;
              }
              .receipt-items-table .addons, .receipt-items-table .remark {
                font-size: 0.98em;
                color: #666;
              }
              .receipt-items-table .remark {
                color: #b36a00;
              }
              .receipt-totals-side {
                flex: 1;
                padding: 32px 32px 32px 16px;
                border-left: 1px solid #eee;
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                min-width: 260px;
                align-items: center;
                overflow: hidden;
              }
              .receipt-totals-side .totals-block {
                width: 100%;
                text-align: left;
              }
              .receipt-totals-side .row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 12px;
                font-size: 1.15em;
                text-align: left;
                width: 100%;
              }
              .receipt-totals-side .row .label {
                font-weight: normal;
                text-align: left;
                flex: 1;
              }
              .receipt-totals-side .row .value {
                font-weight: bold;
                text-align: right;
                flex: 1;
              }
              .receipt-totals-side .dotted {
                border-top: 2px dotted #333;
                margin: 18px 0 18px 0;
                width: 100%;
              }
              .receipt-totals-side .grand-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                font-size: 1.7em;
                font-weight: bold;
                margin: 8px 0 8px 0;
                width: 100%;
              }
              .receipt-totals-side .cash-row,
              .receipt-totals-side .change-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                font-size: 1.15em;
                font-weight: bold;
                margin: 8px 0 8px 0;
                width: 100%;
              }
              /* Fix for global .row>* width: 100% from _grid.scss */
              .receipt-totals-side .row > .label,
              .receipt-totals-side .row > .value {
                width: auto !important;
                max-width: none !important;
                flex: 1 !important;
              }
              .receipt-items-table .receipt-info-text {
                text-align: center;
                font-size: 0.95em;
                color: #444;
                margin: 10px 0 0 0;
              }
              .receipt-items-table .receipt-info-text + .receipt-info-text {
                margin-top: 4px;
              }
              .receipt-items-table .receipt-info-text strong {
                font-weight: bold;
              }
              .receipt-items-table .receipt-table-number {
                font-weight: bold;
                font-size: 1.15em;
                margin-bottom: 12px;
                text-align: center;
              }
              /* Ensure proper display in Electron */
              * {
                box-sizing: border-box;
              }
              /* Override any global styles that might affect the receipt */
              .customer-receipt-modal * {
                box-sizing: border-box !important;
              }
              /* Ensure the receipt is always visible */
              .customer-receipt-modal-bg {
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                right: 0 !important;
                bottom: 0 !important;
                z-index: 9999 !important;
              }
            `}</style>
            <div style={{fontWeight:'bold',textAlign:'center',padding:'8px 0',fontSize:'1.2em',borderBottom:'2px solid #b36a00'}}>PREVIEW RECEIPT</div>
            <div className="receipt-table-number">Table: {tableNumber}</div>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>QTY</th>
                    <th style={{ textAlign: "right" }}>Price ({symbol})</th>
                  </tr>
                </thead>
                <tbody>
                  {previewItemRows.length === 0 && (
                    <tr><td colSpan={3} style={{textAlign:'center',color:'#b36a00'}}>No items found in preview data.</td></tr>
                  )}
                  {previewItemRows.map(([key, item], idx) => {
                    let addOnsTotalPrice = 0;
                    if (item.addOns) {
                      if (item.productCategory === "Combo") {
                        // For combo items: only add prices for custom options (from empty groups)
                        addOnsTotalPrice = item.addOns.reduce((total, addOn) => {
                          const isCustomOption = addOn.isCategoryItem ? false : (addOn.id && !addOn._id);
                          return isCustomOption ? total + parseFloat(addOn.price) : total;
                        }, 0);
                      } else {
                        // For regular items, add all add-on prices
                        addOnsTotalPrice = item.addOns.reduce((total, addOn) => total + parseFloat(addOn.price), 0);
                      }
                    }
                    return (
                      <tr key={key}>
                        <td>
                          {item.productName} {item.discountAble ? '' : '*'}
                          {item.addOns && item.addOns.length > 0 && (
                            <div className="addons">
                              <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {Object.entries(item.addOns.reduce((acc, addOn) => {
                                  if (!acc[addOn.group]) acc[addOn.group] = [];
                                  acc[addOn.group].push(addOn);
                                  return acc;
                                }, {})).map(([group, addOns]) => (
                                  <li key={group}>
                                    <span>{group}: </span>
                                    {addOns.map((a, i) => (
                                      <span key={i}>{a.name} (+{parseFloat(a.price).toFixed(2)}){i < addOns.length-1 ? ', ' : ''}</span>
                                    ))}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {item.remark && <div className="remark">Remark: {item.remark}</div>}
                        </td>
                        <td>{item.totalQuantity}</td>
                        <td style={{ textAlign: "right" }}>{((item.price + addOnsTotalPrice) * item.totalQuantity).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                  {previewServiceRows}
                </tbody>
              </table>
            </div>
            <div className="info-bottom">
              <div className="receipt-info-text">
                Items marked with * are <strong>not eligible</strong> for percentage discount.
              </div>
              <div className="receipt-info-text">
                Service items marked with ** are <strong>only added</strong> at the Grand Total.
              </div>
            </div>
          </div>
          <div className="receipt-totals-side">
            <div className="totals-block">
              <div className="row"><span className="label">Total:</span> <span className="value">{paymentData.totalWithoutTaxes}</span></div>
              <div className="row"><span className="label">Discount { discountType == '%' ? "(" + discountAmount + discountType + ")" : ""}:</span> <span className="value">{paymentData.discount > 0 ? `- ${parseFloat(paymentData.discount).toFixed(2)}` : `${parseFloat(paymentData.discount).toFixed(2)}`}</span></div>
              <div className="row"><span className="label">Sub Total:</span> <span className="value">{parseFloat(paymentData.totalAfterDiscount).toFixed(2)}</span></div>
              {paymentData.taxes && paymentData.taxes.length > 0 &&
                paymentData.taxes
                  .filter(tax => tax.taxRate !== "0%" && tax.taxAmount !== "0.00")
                  .filter(tax => takeAwayTable ? tax.taxValidForTakeAway : true)
                  .map((tax, idx) => (
                    <div className="row" key={idx}>
                      <span className="label">{tax.taxName} ({tax.taxRate}):</span>
                      <span className="value">{tax.taxAmount}</span>
                    </div>
                  ))
              }
              <div className="row"><span className="label">Pre-Rounding Total:</span> <span className="value">{parseFloat(paymentData.totalWithServices).toFixed(2)}</span></div>
              <div className="row"><span className="label">Rounding:</span> <span className="value">{paymentData.rounding < 0 ? `- ${Math.abs(paymentData.rounding).toFixed(2)}` : `${Math.abs(paymentData.rounding).toFixed(2)}`}</span></div>
              <div className="dotted"></div>
              <div className="grand-row"><span className="label">Grand Total:</span> <span className="value">{parseFloat(paymentData.roundedTotalWithServices).toFixed(2)}</span></div>
              {Number(paymentData.deposit) !== 0 && (
                <>
                  <div className="grand-row">
                    <span className="label">Deposit:</span>
                    <span className="value">- {paymentData.deposit}</span>
                  </div>
                  <div className="grand-row">
                    <span className="label">Payable Amount:</span>
                    <span className="value">{previewPayableAmount}</span>
                  </div>
                </>
              )}
              <div className="dotted"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (!invoiceData || !invoiceData.orderDetails) {
    return <div></div>;
  }

  const orderDetails = invoiceData.orderDetails;
  // Flatten and group items for display
  const itemRows = Object.entries(
    orderDetails.flatMap(order => Array.isArray(order.order_items) ? order.order_items : []).reduce((acc, item) => {
      const addOnNames = item.product.addOns.map(a => a.name).sort().join(", ");
      const key = `${item.product.name}-${item.remarks || ""}-${addOnNames}`;
      acc[key] = acc[key] || {
        productName: item.product.name,
        addOns: item.product.addOns,
        remark: item.remarks,
        totalQuantity: 0,
        discountAble: Boolean(item.product.discountAble !== false),
        price: Number(item.product.price),
        productCategory: item.product.category,
      };
      acc[key].totalQuantity += Number(item.quantity);
      return acc;
    }, {})
  );

  
  // After main items, render service items (marked with **)
  const serviceRows = (services || []).filter(s => s.quantity > 0).map((service, idx) => (
    <tr key={"service-" + idx}>
      <td>{service.name} **</td>
      <td>{service.quantity}</td>
      <td style={{ textAlign: "right" }}>{service.totalPrice?.toFixed ? service.totalPrice.toFixed(2) : Number(service.totalPrice || 0).toFixed(2)}</td>
    </tr>
  ));

  const previewGrandTotal = parseFloat(paymentData.roundedTotalWithServices) || 0;
  const previewDeposit = parseFloat(paymentData.deposit) || 0;
  const previewPayableAmount = (previewGrandTotal - previewDeposit).toFixed(2);

  return (
    <div className="customer-receipt-modal-bg">
      <style>{`
        .customer-receipt-modal-bg {
          position: fixed;
          top: 0; left: 0;
          width: 100%; height: 100%;
          min-height: 0;
          z-index: 9999;
          background: rgba(255,255,255,0.0); /* transparent, video visible */
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: auto;
        }
        .customer-receipt-modal {
          background: #fff;
          border-radius: 18px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          max-width: 900px;
          width: 90%;
          max-height: 90%;
          min-width: 350px;
          display: flex;
          flex-direction: row;
          overflow: hidden;
          height: 90%;
          min-height: 0;
          position: relative;
          z-index: 10000;
        }
        .receipt-items-table {
          flex: 2;
          padding: 5px 20px 24px 20px;
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 0;
          overflow: hidden;
        }
        .receipt-items-table .table-scroll {
          flex: 1 1 auto;
          overflow-y: auto;
          overflow-x: hidden;
        }
        .receipt-items-table .info-bottom {
          flex-shrink: 0;
        }
        .receipt-items-table table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
        }
        .receipt-items-table th, .receipt-items-table td {
          padding: 8px 6px;
          font-size: 1.1em;
          border-bottom: 1px solid #eee;
          text-align: left;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        .receipt-items-table th {
          font-weight: bold;
          background: #f7f7f7;
        }
        .receipt-items-table .addons, .receipt-items-table .remark {
          font-size: 0.98em;
          color: #666;
        }
        .receipt-items-table .remark {
          color: #b36a00;
        }
        .receipt-totals-side {
          flex: 1;
          padding: 32px 32px 32px 16px;
          border-left: 1px solid #eee;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          min-width: 260px;
          align-items: center;
          overflow: hidden;
        }
        .receipt-totals-side .totals-block {
          width: 100%;
          text-align: left;
        }
        .receipt-totals-side .row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          font-size: 1.15em;
          text-align: left;
          width: 100%;
        }
        .receipt-totals-side .row .label {
          font-weight: normal;
          text-align: left;
          flex: 1;
        }
        .receipt-totals-side .row .value {
          font-weight: bold;
          text-align: right;
          flex: 1;
        }
        .receipt-totals-side .dotted {
          border-top: 2px dotted #333;
          margin: 18px 0 18px 0;
          width: 100%;
        }
        .receipt-totals-side .grand-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 1.7em;
          font-weight: bold;
          margin: 8px 0 8px 0;
          width: 100%;
        }
        .receipt-totals-side .cash-row,
        .receipt-totals-side .change-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 1.15em;
          font-weight: bold;
          margin: 8px 0 8px 0;
          width: 100%;
        }
        /* Fix for global .row>* width: 100% from _grid.scss */
        .receipt-totals-side .row > .label,
        .receipt-totals-side .row > .value {
          width: auto !important;
          max-width: none !important;
          flex: 1 !important;
        }
        .receipt-items-table .receipt-info-text {
          text-align: center;
          font-size: 0.95em;
          color: #444;
          margin: 10px 0 0 0;
        }
        .receipt-items-table .receipt-info-text + .receipt-info-text {
          margin-top: 4px;
        }
        .receipt-items-table .receipt-info-text strong {
          font-weight: bold;
        }
        .receipt-items-table .receipt-table-number {
          font-weight: bold;
          font-size: 1.15em;
          margin-bottom: 12px;
          text-align: center;
        }
        /* Ensure proper display in Electron */
        * {
          box-sizing: border-box;
        }
        /* Override any global styles that might affect the receipt */
        .customer-receipt-modal * {
          box-sizing: border-box !important;
        }
        /* Ensure the receipt is always visible */
        .customer-receipt-modal-bg {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          z-index: 9999 !important;
        }
      `}</style>
      <div className="customer-receipt-modal">
        <div className="receipt-items-table">
        <div style={{fontWeight:'bold',textAlign:'center',padding:'8px 0',fontSize:'1.2em',borderBottom:'2px solid #b36a00'}}>FINAL RECEIPT</div>
          <div className="receipt-table-number">Table: {tableNumber}</div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>QTY</th>
                  <th style={{ textAlign: "right" }}>Price ({symbol})</th>
                </tr>
              </thead>
              <tbody>
                {itemRows.map(([key, item], idx) => {
                  let addOnsTotalPrice = 0;
                  if (item.addOns) {
                    if (item.productCategory === "Combo") {
                      // For combo items: only add prices for custom options (from empty groups)
                      addOnsTotalPrice = item.addOns.reduce((total, addOn) => {
                        const isCustomOption = addOn.id && !addOn._id;
                        return isCustomOption ? total + parseFloat(addOn.price) : total;
                      }, 0);
                    } else {
                      // For regular items, add all add-on prices
                      addOnsTotalPrice = item.addOns.reduce((total, addOn) => total + parseFloat(addOn.price), 0);
                    }
                  }
                  return (
                    <tr key={key}>
                      <td>
                        {item.productName} {item.discountAble ? '' : '*'}
                        {item.addOns && item.addOns.length > 0 && (
                          <div className="addons">
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                              {Object.entries(item.addOns.reduce((acc, addOn) => {
                                if (!acc[addOn.group]) acc[addOn.group] = [];
                                acc[addOn.group].push(addOn);
                                return acc;
                              }, {})).map(([group, addOns]) => (
                                <li key={group}>
                                  <span>{group}: </span>
                                  {addOns.map((a, i) => (
                                    <span key={i}>{a.name} (+{parseFloat(a.price).toFixed(2)}){i < addOns.length-1 ? ', ' : ''}</span>
                                  ))}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {item.remark && <div className="remark">Remark: {item.remark}</div>}
                      </td>
                      <td>{item.totalQuantity}</td>
                      <td style={{ textAlign: "right" }}>{((item.price + addOnsTotalPrice) * item.totalQuantity).toFixed(2)}</td>
                    </tr>
                  );
                })}
                {serviceRows}
              </tbody>
            </table>
          </div>
          <div className="info-bottom">
            <div className="receipt-info-text">
              Items marked with * are <strong>not eligible</strong> for percentage discount.
            </div>
            <div className="receipt-info-text">
              Service items marked with ** are <strong>only added</strong> at the Grand Total.
            </div>
            <div className="receipt-info-text">
            Thank you! Please come again :)
            </div>
          </div>
        </div>
        <div className="receipt-totals-side">
          <div className="totals-block">
            <div className="row"><span className="label">Total:</span> <span className="value">{totalWithoutTaxes}</span></div>
            <div className="row"><span className="label">Discount { discountType == '%' ? "(" + discountAmount + discountType + ")" : ""}:</span> <span className="value">{discount > 0 ? `- ${parseFloat(discount).toFixed(2)}` : `${parseFloat(discount).toFixed(2)}`}</span></div>
            <div className="row"><span className="label">Sub Total:</span> <span className="value">{parseFloat(totalAfterDiscount).toFixed(2)}</span></div>
            {taxes && taxes.length > 0 && taxes.filter(tax => takeAwayTable ? tax.taxValidForTakeAway : true).map((tax, idx) => (
              <div className="row" key={idx}><span className="label">{tax.taxName} ({tax.taxRate}):</span> <span className="value">{tax.taxAmount}</span></div>
            ))}
            <div className="row"><span className="label">Pre-Rounding Total:</span> <span className="value">{parseFloat(totalWithServices).toFixed(2)}</span></div>
            <div className="row"><span className="label">Rounding:</span> <span className="value">{rounding < 0 ? `- ${Math.abs(rounding).toFixed(2)}` : `${Math.abs(rounding).toFixed(2)}`}</span></div>
            <div className="dotted"></div>
            <div className="grand-row"><span className="label">Grand Total:</span> <span className="value">{parseFloat(roundedTotalWithServices).toFixed(2)}</span></div>
            {Number(paymentData.deposit) !== 0 && (
                <>
                  <div className="grand-row">
                    <span className="label">Deposit:</span>
                    <span className="value">- {paymentData.deposit}</span>
                  </div>
                  <div className="grand-row">
                    <span className="label">Payable Amount:</span>
                    <span className="value">{previewPayableAmount}</span>
                  </div>
                </>
              )}
              <div className="dotted"></div>
            {paymentMethod === "Cash" && (
              <div className="cash-row"><span className="label">Cash:</span> <span className="value">{cashAmount}</span></div>
            )}
            {paymentMethod === "Cash" && (
              <div className="change-row"><span className="label">Change:</span> <span className="value">{(parseFloat(balance) < 0 ? 0 : Math.round(balance * 20) / 20).toFixed(2)}</span></div>
            )}
            {/* Only show Payment Method if not Cash */}
            {paymentMethod !== "Cash" && (
              <div className="change-row"><span className="label">Payment Method:</span> <span className="value">{paymentMethodMap[paymentMethod] || paymentMethod || "N/A"}</span></div>
            )}
            {transactionId && paymentMethod !== "Cash" && (
              <div className="change-row"><span className="label">Transaction ID:</span> <span className="value">{transactionId}</span></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerReceipt; 